import type { Vec2, Ring } from '../../../geo/types';
import type { LinearRGB } from '../../../core/color';
import type { Tower, WallRun } from '../../../data/types';
import type { MeshBuilder, VertexStyle, Frame } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import { layoutToWorld } from '../../../core/coords';
import { distancePointSegment } from '../../../geo/polygon';
import { insetConvexRing } from '../planGeometry';
import {
  horizontalCap,
  pyramidOverRing,
  ringPoints,
  triangulateRing,
  orientedFace,
  planPoint,
} from '../solids';
import { FORTIFICATION_ROOF, towerHasSlateRoof } from './roofs';
import { buildCrenellatedTower, crenellatedPlanRing, isCrenellatedTower } from './crenellatedTower';
import { NO_WATER, type WaterTest } from './moat';
import { buildTowerMasonry } from './towerMasonry';
import { buildMontelbaanDormer } from './montelbaanDormer';
import { buildLastageBlockhouse, LASTAGE_BLOCKHOUSES } from './lastageBlockhouses';

const SEGMENTS: Readonly<Record<Tower['shape'], number>> = {
  round: 16,
  'half-round': 8,
  octagonal: 8,
  square: 4,
};

export interface TowerStyles {
  readonly wall: VertexStyle;
  readonly roof: VertexStyle;
}

/** The wall runs a tower may stand on and the water beside them. */
export interface TowerSetting {
  readonly walls: readonly WallRun[];
  readonly isWater: WaterTest;
  /** Timber Lastage bodies use their painted wooden material rather than tower masonry. */
  readonly timberColor?: LinearRGB;
}

const NO_SETTING: TowerSetting = { walls: [], isWater: NO_WATER };

export interface TowerFacing {
  readonly yaw: number;
  /** A half-round tower whose wall has water on neither side within twice the tower's radius. */
  readonly withoutMoat: boolean;
}

/* Probes a quarter radius apart along the wall's normal, out to twice the radius on either side. */
const MOAT_PROBES = 8;

function wallNormal(a: Vec2, b: Vec2): Vec2 | null {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = Math.hypot(dx, dy);
  return len < 0.001 ? null : [-dy / len, dx / len];
}

interface NearestWall {
  readonly a: Vec2;
  readonly normal: Vec2;
}

function nearestWall(pos: Vec2, walls: readonly WallRun[]): NearestWall | null {
  let best = Infinity;
  let nearest: NearestWall | null = null;
  for (const wall of walls) {
    for (let i = 0; i < wall.path.length - 1; i++) {
      const a = wall.path[i]!;
      const b = wall.path[i + 1]!;
      const d = distancePointSegment(pos, { a, b });
      const normal = d < best ? wallNormal(a, b) : null;
      if (normal) {
        best = d;
        nearest = { a, normal };
      }
    }
  }
  return nearest;
}

function waterCount(tower: Tower, direction: Vec2, isWater: WaterTest): number {
  let count = 0;
  for (let k = 1; k <= MOAT_PROBES; k++) {
    const d = (k * tower.radius) / 4;
    if (isWater([tower.pos[0] + direction[0] * d, tower.pos[1] + direction[1] * d])) count++;
  }
  return count;
}

const yawOf = (direction: Vec2): number => Math.atan2(direction[1], direction[0]);

/*
 * A tower whose surviving footprint documents its facing takes the layout's heading: the rules below
 * only infer the side, and the present-day shore can mislead them. Otherwise a half-round tower
 * turns its round side to the side of its wall that holds the moat; the others face east. The
 * layout sets the towers on the wall line, 0.005-0.058 m off it, so the sign of that offset says
 * nothing about the side. Where the wall has water on neither side, the round side turns away from
 * the wall line as the tower stands off it.
 */
export function towerFacing(tower: Tower, setting: TowerSetting = NO_SETTING): TowerFacing {
  if (tower.heading !== undefined) return { yaw: tower.heading, withoutMoat: false };
  if (tower.shape !== 'half-round') return { yaw: 0, withoutMoat: false };
  const wall = nearestWall(tower.pos, setting.walls);
  if (!wall) return { yaw: 0, withoutMoat: true };
  const { normal } = wall;
  const opposite: Vec2 = [-normal[0], -normal[1]];
  const lean =
    waterCount(tower, normal, setting.isWater) - waterCount(tower, opposite, setting.isWater);
  if (lean !== 0) return { yaw: yawOf(lean > 0 ? normal : opposite), withoutMoat: false };
  const offset = normal[0] * (tower.pos[0] - wall.a[0]) + normal[1] * (tower.pos[1] - wall.a[1]);
  return { yaw: yawOf(offset >= 0 ? normal : opposite), withoutMoat: true };
}

export function towerHeading(tower: Tower, setting: TowerSetting = NO_SETTING): number {
  return towerFacing(tower, setting).yaw;
}

/** Plan outline in the tower frame; a half-round tower's flat back lies on u = 0. */
export function towerPlanRing(tower: Tower): Ring {
  const r = tower.radius;
  if (isCrenellatedTower(tower)) return crenellatedPlanRing(tower);
  switch (tower.shape) {
    case 'round':
      return ringPoints([0, 0], r, SEGMENTS.round, 0);
    case 'octagonal':
      return ringPoints([0, 0], r, SEGMENTS.octagonal, 0);
    case 'square':
      return ringPoints([0, 0], r * Math.SQRT2, SEGMENTS.square, Math.PI / 4);
    case 'half-round': {
      const n = SEGMENTS['half-round'];
      return Array.from({ length: n + 1 }, (_, i): Vec2 => {
        const a = -Math.PI / 2 + (Math.PI * i) / n;
        return [Math.cos(a) * r, Math.sin(a) * r];
      });
    }
  }
}

export function towerPolygon(tower: Tower, setting: TowerSetting = NO_SETTING): Ring {
  const yaw = towerHeading(tower, setting);
  const c = Math.cos(yaw);
  const s = Math.sin(yaw);
  return towerPlanRing(tower).map((p): Vec2 => [
    tower.pos[0] + p[0] * c - p[1] * s,
    tower.pos[1] + p[0] * s + p[1] * c,
  ]);
}

function scaledRing(ring: Ring, k: number): Ring {
  return ring.map((p): Vec2 => [p[0] * k, p[1] * k]);
}

function buildFlatTop(
  builder: MeshBuilder,
  styles: TowerStyles,
  frame: Frame,
  ring: Ring,
  height: number,
  tower: Tower
): void {
  const { parapetHeight, parapetThickness } = FORTIFICATION_ROOF.tower.flat;
  const top = height + parapetHeight;
  const inner = insetConvexRing(ring, parapetThickness);
  buildTowerMasonry(builder, styles.wall, frame, tower, ring, top, false);
  builder.prism(styles.wall, frame, [...inner].reverse(), height, top, {
    top: false,
    bottom: false,
    sides: true,
  });
  for (const tri of triangulateRing(ring, [inner])) {
    orientedFace(
      builder,
      styles.wall,
      frame,
      tri.map((q) => planPoint(q[0], top, q[1])),
      [0, 1, 0]
    );
  }
  horizontalCap(builder, styles.roof, frame, inner, height, true);
}

function buildRoofedTower(
  builder: MeshBuilder,
  styles: TowerStyles,
  frame: Frame,
  tower: Tower,
  ring: Ring
): void {
  const { radius: r, height, roof } = tower;
  const spec = FORTIFICATION_ROOF.tower;
  const spire = roof === 'spire';
  buildTowerMasonry(builder, styles.wall, frame, tower, ring, height, spire);
  const rise =
    r *
    (tower.id === 'tower-jan-roodenpoort'
      ? 0.65
      : spire
        ? spec.spire.riseRatio
        : roof === 'hip'
          ? spec.hip.riseRatio
          : tower.height < 9
            ? 0.85
            : spec.cone.riseRatio);
  const base = spire ? scaledRing(ring, spec.spire.baseRatio) : ring;
  pyramidOverRing(builder, styles, frame, base, height, { plan: [0, 0], y: height + rise });
  buildMontelbaanDormer(builder, frame, tower, styles);
}

export function buildTower(
  builder: MeshBuilder,
  tower: Tower,
  wallColor: LinearRGB,
  roofColor: LinearRGB,
  edgeId: number,
  seed: number,
  setting: TowerSetting = NO_SETTING
): number {
  const frame: Frame = { origin: layoutToWorld(tower.pos, 0), yaw: towerHeading(tower, setting) };
  const base = { edgeId, seed, strokeAngle: 0, exaggeration: { base: 0, anchor: tower.pos } };
  const before = builder.triangleCount;
  if (isCrenellatedTower(tower)) {
    buildCrenellatedTower(builder, frame, tower, {
      wall: {
        ...base,
        color: wallColor,
        surface: SURFACE.stone,
        extent: [tower.radius * 2, tower.height],
      },
      roof: {
        ...base,
        color: roofColor,
        surface: SURFACE.roofSlate,
        extent: [tower.radius * 2, tower.radius],
      },
    });
    return builder.triangleCount - before;
  }
  const styles: TowerStyles = {
    wall: {
      ...base,
      color: wallColor,
      surface: SURFACE.stone,
      extent: [tower.radius * 2, tower.height],
    },
    roof: {
      ...base,
      color: roofColor,
      surface: towerHasSlateRoof(tower) ? SURFACE.roofSlate : SURFACE.roofTile,
      extent: [tower.radius * 2, tower.radius * 1.5],
    },
  };
  const ring = towerPlanRing(tower);
  if (LASTAGE_BLOCKHOUSES.has(tower.id)) buildLastageBlockhouse(builder, frame, tower, ring,
    { ...styles, timber: { ...styles.wall, surface: SURFACE.wood,
      color: setting.timberColor ?? wallColor } });
  else if (tower.roof === 'flat') buildFlatTop(builder, styles, frame, ring, tower.height, tower);
  else buildRoofedTower(builder, styles, frame, tower, ring);
  return builder.triangleCount - before;
}
